/**
 * Máscaras de documento, telefone, CEP e data.
 *
 * A máscara é PROGRESSIVA — aparece enquanto o operador digita, não só quando
 * o campo fica pronto. Ele confere o número no formato em que o número existe
 * no mundo (45.216.669/0001-53), e não numa fila de 14 dígitos onde ninguém
 * enxerga um zero a mais.
 *
 * Funções puras, de propósito: dá para testá-las sem montar componente.
 */

import { onlyDigits } from '@amai/contracts';

export function maskDocument(digits: string): string {
  const d = digits.slice(0, 14);
  if (d.length <= 11) {
    // CPF: 000.000.000-00
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  }
  // CNPJ: 00.000.000/0000-00
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
}

export function maskPhone(digits: string): string {
  const d = digits.slice(0, 11);
  if (d.length <= 2) return d.replace(/^(\d{1,2})/, '($1');
  if (d.length <= 6) return d.replace(/^(\d{2})(\d+)/, '($1) $2');
  if (d.length <= 10) return d.replace(/^(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
  return d.replace(/^(\d{2})(\d{5})(\d+)/, '($1) $2-$3');
}

export function maskCep(digits: string): string {
  const d = digits.slice(0, 8);
  return d.replace(/^(\d{5})(\d+)/, '$1-$2');
}

/**
 * Data em dd/mm/aaaa, progressiva enquanto se digita.
 *
 * O campo nativo do navegador mostra a ordem do idioma do SISTEMA, não a do
 * parque: numa máquina em inglês o operador via mm/dd/aaaa e lançava 09/12
 * achando que era 9 de dezembro. Aqui a ordem é sempre a nossa.
 */
export function maskDate(digits: string): string {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * dd/mm/aaaa → aaaa-mm-dd, o formato que a API recebe.
 *
 * Devolve '' para o que não é uma data que exista: 31/02 e 00/00/0000 não
 * podem virar ISO só porque têm oito dígitos.
 */
export function dateBRToISO(text: string): string {
  const d = onlyDigits(text ?? '');
  if (d.length !== 8) return '';

  const day = Number(d.slice(0, 2));
  const month = Number(d.slice(2, 4));
  const year = Number(d.slice(4));

  if (month < 1 || month > 12 || day < 1) return '';
  // Ano de dois dígitos não é expandido: 15/03/98 pode ser 1998 (nascimento)
  // ou 2098, e o sistema não tem como adivinhar qual. Melhor recusar.
  if (year < 1900 || year > 2999) return '';

  // Dia 0 do mês seguinte é o último dia deste — pega ano bissexto de graça.
  const ultimoDia = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > ultimoDia) return '';

  return `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
}

/** aaaa-mm-dd → dd/mm/aaaa. Vazio para o que não estiver nesse formato. */
export function isoToDateBR(iso: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}
